import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture || null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Fetch latest user data from server
  const fetchLatestUserData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/auth/me');
      const latestUser = response.data;
      
      setCurrentUser(latestUser);
      setProfileData({
        full_name: latestUser.full_name || '',
        email: latestUser.email || '',
        role: latestUser.role || '',
        phone: latestUser.phone || ''
      });
      setProfilePicture(latestUser.profile_picture || null);
      
      // Update auth context with latest data
      updateUser(latestUser);
      
      toast.success('Profile refreshed successfully');
      
    } catch (error) {
      console.error('Error fetching latest user data:', error);
      toast.error('Failed to refresh profile data');
      // If API fails, use cached user data
      setCurrentUser(user);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on component mount
  useEffect(() => {
    fetchLatestUserData();
  }, []); // Run once when component mounts

  // Refresh data when window regains focus (e.g., after admin updates in another tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchLatestUserData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Administrator',
      'accountant': 'Accountant',
      'operating_head': 'Operating Head',
      'counselor': 'Counselor'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'admin': 'bg-red-100 text-red-800',
      'operating_head': 'bg-purple-100 text-purple-800',
      'accountant': 'bg-green-100 text-green-800',
      'counselor': 'bg-blue-100 text-blue-800'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      const response = await apiClient.put('/profile', profileData);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      
      // Update user context with new data
      if (response.data.user) {
        updateUser(response.data.user);
      }
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Profile update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingPicture(true);
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result;
          
          const response = await apiClient.post('/profile/picture', {
            image_data: base64Data,
            filename: file.name
          });
          
          const updatedProfilePictureUrl = response.data.profile_picture_url;
          setProfilePicture(updatedProfilePictureUrl);
          
          // Update user context and current user state with new profile picture
          const updatedUser = { ...currentUser, profile_picture: updatedProfilePictureUrl };
          updateUser(updatedUser);
          setCurrentUser(updatedUser);
          
          toast.success('Profile picture updated successfully!');
          
        } catch (error) {
          toast.error('Failed to upload profile picture');
          console.error('Upload error:', error);
        } finally {
          setUploadingPicture(false);
        }
      };
      
      reader.readAsDataURL(file);
      
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingPicture(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      // API endpoint to change password - to be implemented
      // await apiClient.put('/profile/password', {
      //   current_password: passwordData.current_password,
      //   new_password: passwordData.new_password
      // });
      toast.success('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-500 mt-1">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Profile Information</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchLatestUserData}
                disabled={loading}
                className="h-8 w-8 p-0"
                title="Refresh profile data"
              >
                <i className={`fas fa-sync-alt ${loading ? 'animate-spin' : ''}`}></i>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  {profilePicture && <AvatarImage src={profilePicture} alt="Profile" />}
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-2xl">
                    {currentUser?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                
                {/* Upload button overlay */}
                <div className="absolute -bottom-2 -right-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    id="profile-picture-upload"
                  />
                  <label
                    htmlFor="profile-picture-upload"
                    className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-2 shadow-lg transition-colors"
                  >
                    {uploadingPicture ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <i className="fas fa-camera text-xs"></i>
                    )}
                  </label>
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">{currentUser?.full_name}</h3>
                <p className="text-sm text-gray-500">{currentUser?.email}</p>
              </div>

              <Badge className={`${getRoleColor(currentUser?.role)}`}>
                {getRoleDisplayName(currentUser?.role)}
              </Badge>

              {/* Account Status Warning */}
              {!currentUser?.is_active && (
                <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <i className="fas fa-exclamation-triangle text-red-500"></i>
                    <div>
                      <p className="text-sm font-medium text-red-800">Account Disabled</p>
                      <p className="text-xs text-red-600">Your account has been disabled by an administrator. Please contact support for assistance.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="w-full pt-4 space-y-2 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">User ID:</span>
                  <span className="text-gray-900 font-mono text-xs">{user?.id?.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status:</span>
                  <Badge variant={currentUser?.is_active ? 'success' : 'destructive'} className={currentUser?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {currentUser?.is_active ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
                {user?.partner_office && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Office:</span>
                    <span className="text-gray-900">{user?.partner_office}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Personal Details</CardTitle>
                <CardDescription>Update your personal information</CardDescription>
              </div>
              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  <i className="fas fa-edit mr-2"></i>
                  Edit Profile
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  disabled={!isEditing}
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  disabled={!isEditing}
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  disabled={!isEditing}
                />
              </div>
              
              <div>
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={getRoleDisplayName(user?.role)}
                  disabled
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsEditing(false);
                    setProfileData({
                      full_name: user?.full_name || '',
                      email: user?.email || '',
                      phone: user?.phone || ''
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateProfile} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-md space-y-4">
            <div>
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder="Enter current password"
              />
            </div>
            
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="Enter new password"
              />
            </div>
            
            <div>
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="Confirm new password"
              />
            </div>

            <Button 
              onClick={handleChangePassword} 
              disabled={loading || !passwordData.current_password || !passwordData.new_password}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
